"use client";

import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";

const inputClass =
  "w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600";

export function DeleteAccountRequestForm() {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [details, setDetails] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-900">Request received</p>
        <p className="mt-2 text-sm text-gray-700">
          If an account exists for that email, we will delete it and associated
          personal data. We typically complete requests within 30 days and will
          contact you only if we need more information to identify the account.
        </p>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!confirm) {
      setError("Confirm that you want the account and associated data deleted.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/account/deletion-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          username,
          details,
          confirm: true,
          website,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(
          data.error ||
            "We could not send the request. Email privacy@certlynx.com instead.",
        );
        return;
      }
      setDone(true);
    } catch {
      setError(
        "Network error. Try again, or email privacy@certlynx.com to request deletion.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="hidden" aria-hidden="true">
        <input
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-900" htmlFor="deletion-email">
          Account email
        </label>
        <input
          id="deletion-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          className={inputClass}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-900" htmlFor="deletion-username">
          Username <span className="font-normal text-gray-500">(optional)</span>
        </label>
        <input
          id="deletion-username"
          name="username"
          autoComplete="username"
          className={inputClass}
          placeholder="If you remember it"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-900" htmlFor="deletion-details">
          Additional details <span className="font-normal text-gray-500">(optional)</span>
        </label>
        <textarea
          id="deletion-details"
          name="details"
          rows={4}
          className={`${inputClass} resize-none`}
          placeholder="Anything that helps us identify the account"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
      </div>
      <label className="flex items-start gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          className="mt-1"
          checked={confirm}
          onChange={(e) => setConfirm(e.target.checked)}
        />
        <span>
          I request that CertLynx delete my account and associated personal data.
        </span>
      </label>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={submitting}>
        {submitting ? "Sending…" : "Request account deletion"}
      </Button>
    </form>
  );
}
